// ProgramWizard — the 5-step builder orchestrator (slice 4a-3).
//
// Owns: the current step, the useBuilder working-state controller, the
// debounced preview projection, Next/Back navigation, and the activation
// flow. Steps render into the left column; PassPreviewPanel sits on the right
// (desktop) or as a bottom sheet (mobile).
//
// When `cosmeticOnly` is true (an already-active program editing appearance),
// the wizard restricts itself to steps 3 + 4 and there is no activation step.

import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

import type { PassPreviewProgram } from '../../shared/pass-preview/PassPreview.js'
import { logoSrc } from '../logoSrc.js'
import { OwnerApiError } from '../ownerApi.js'
import { useOwnerSession } from '../hooks/useOwnerSession.js'
import { ActivateConfirmModal } from './ActivateConfirmModal.js'
import { PassPreviewPanel } from './PassPreviewPanel.js'
import {
  EVENT_TICKET_WIZARD_STEPS,
  MEMBERSHIP_WIZARD_STEPS,
  WIZARD_STEPS,
  isEventTicketDraft,
  isMembershipDraft,
  type WizardStepKey,
} from './builderState.js'
import { draftToPreview } from './previewMapper.js'
import { useBuilder } from './hooks/useBuilder.js'
import { useActivateProgram } from './hooks/useProgram.js'
import { StepBack } from './steps/StepBack.js'
import { StepEvent } from './steps/StepEvent.js'
import { StepEventReview } from './steps/StepEventReview.js'
import { StepFront } from './steps/StepFront.js'
import { StepMembership } from './steps/StepMembership.js'
import { StepMembershipExpiry } from './steps/StepMembershipExpiry.js'
import { StepMembershipReview } from './steps/StepMembershipReview.js'
import { StepOnboarding } from './steps/StepOnboarding.js'
import { StepProgram } from './steps/StepProgram.js'
import { StepReview } from './steps/StepReview.js'
import type { Program } from './types.js'
import './programBuilder.css'

// 200ms debounce on the preview projection — keeps the memoized PassPreview
// from repainting on every keystroke (brief: "Updates debounced 200ms").
function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), ms)
    return () => clearTimeout(id)
  }, [value, ms])
  return debounced
}

export function ProgramWizard({
  program,
  cosmeticOnly = false,
  devUnlock = false,
}: {
  program: Program
  /** Active program editing appearance — restricts to steps 3+4, no activate. */
  cosmeticOnly?: boolean
  /** Staging-only escape hatch — when true, render a "dev unlock" banner so
   *  the tester knows structural fields are editable on this active program. */
  devUnlock?: boolean
}) {
  const navigate = useNavigate()
  const { data: me } = useOwnerSession()
  const builder = useBuilder(program)
  const activate = useActivateProgram(program.id)
  const [confirmOpen, setConfirmOpen] = useState(false)

  // SLICE-MEMBERSHIP-PASS — membership programs walk a different (simpler) step
  // list. Both pass types share the same cosmetic steps (front + back), so the
  // cosmeticOnly restriction is identical across types.
  const isMembership = isMembershipDraft(builder.draft)
  const isEvent = isEventTicketDraft(builder.draft)

  // Which steps this run of the wizard exposes.
  const steps = useMemo(() => {
    const all = isEvent
      ? EVENT_TICKET_WIZARD_STEPS
      : isMembership
        ? MEMBERSHIP_WIZARD_STEPS
        : WIZARD_STEPS
    return cosmeticOnly
      ? all.filter((s) => s.key === 'front' || s.key === 'back')
      : all
  }, [cosmeticOnly, isMembership, isEvent])
  const [stepIndex, setStepIndex] = useState(0)
  // Clamp to the available steps. `steps` can shrink under a stale index — e.g.
  // a program flips to active mid-session (cosmeticOnly drops the wizard from 5
  // steps to 2) right before doActivate navigates away. Without the clamp,
  // steps[stepIndex] is undefined and the render crashes reading `.key`.
  const safeStepIndex = Math.min(stepIndex, steps.length - 1)
  const current = steps[safeStepIndex]!

  // The preview projection — debounced so the memoized PassPreview is calm.
  // `filledCount = 2` so the owner sees what a partially-stamped pass looks
  // like (the filled stamp icon, and the star badges on still-empty milestone
  // slots past the current count). 4a-4's enrollment / card view will pass a
  // real count instead.
  const previewSource = useMemo<PassPreviewProgram>(
    () =>
      draftToPreview(
        builder.draft,
        {
          businessName: me?.tenant.name ?? 'Your business',
          logoUrl:
            me?.tenant && me
              ? logoSrc(
                  me.tenant.logo_url,
                  me.default_logo_url,
                  me.tenant.logo_updated_at,
                )
              : null,
        },
        2,
      ),
    [builder.draft, me],
  )
  const preview = useDebounced(previewSource, 200)

  async function goToStep(index: number) {
    // Flush the autosave before moving so a step never shows stale server state.
    await builder.flush()
    setStepIndex(index)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  function jumpToStepKey(key: WizardStepKey) {
    const idx = steps.findIndex((s) => s.key === key)
    if (idx >= 0) void goToStep(idx)
  }

  async function doActivate() {
    await builder.flush()
    try {
      await activate.mutateAsync()
      toast.success('Program activated. Customers can now enroll')
      setConfirmOpen(false)
      navigate('/owner/program', { replace: true })
    } catch (err) {
      setConfirmOpen(false)
      if (
        err instanceof OwnerApiError &&
        err.code === 'PROGRAM_ACTIVATION_INCOMPLETE'
      ) {
        const gaps = Array.isArray(err.details?.gaps)
          ? (err.details.gaps as string[])
          : []
        toast.error(
          gaps.length
            ? `Can't activate yet: ${gaps[0]}`
            : "Can't activate yet. Some required info is missing.",
        )
      } else {
        toast.error(
          err instanceof OwnerApiError
            ? err.userMessage
            : 'Could not activate the program.',
        )
      }
    }
  }

  const isLast = safeStepIndex === steps.length - 1

  // Which view the Instant Preview should follow on a step change. The
  // sign-up step opens the Enroll preview (the customer's enrollment
  // screen — the only thing that step affects); the back-of-pass step
  // opens the back; everything else opens the front. The owner can still
  // flip the tabs manually — this only re-syncs on step change.
  const previewSide: 'front' | 'back' | 'enroll' =
    current.key === 'onboarding'
      ? 'enroll'
      : current.key === 'back'
        ? 'back'
        : 'front'

  return (
    <div className="program-builder">
      <div className="program-builder-main">
        {devUnlock && program.status === 'active' && (
          <div className="program-dev-unlock-banner" role="status">
            <strong>Dev unlock active —</strong> structural fields (stamps,
            milestones, completion behavior, onboarding) are editable on this
            active program. Existing customer cards keep their original
            snapshot; re-enroll a test customer to see structural changes.
          </div>
        )}

        {/* Step X of Y + the current step's name — the numbered rail below is
            a jump-nav, not a label; on its own "1 2 3 4 5" tells the owner
            nothing about where they are or what's left. This line does. */}
        <p className="program-step-progress">
          Step {safeStepIndex + 1} of {steps.length} · {current.label}
        </p>

        {/* Step rail */}
        <ol className="program-step-rail" aria-label="Builder steps">
          {steps.map((s, i) => (
            <li
              key={s.key}
              className={`program-step-rail-item${
                i === stepIndex ? ' is-current' : ''
              }${i < stepIndex ? ' is-done' : ''}`}
            >
              <button type="button" onClick={() => void goToStep(i)}>
                <span className="program-step-rail-num">{i + 1}</span>
                <span className="program-step-rail-label">{s.label}</span>
              </button>
            </li>
          ))}
        </ol>

        {/* Save status */}
        <div className="program-save-status" aria-live="polite">
          {builder.saveState.status === 'saving' && <span>Saving…</span>}
          {builder.saveState.status === 'saved' && (
            <span className="is-ok">All changes saved</span>
          )}
          {builder.saveState.status === 'error' && (
            <span className="is-error">{builder.saveState.message}</span>
          )}
        </div>

        {/* Current step */}
        <div className="program-step-host">
          {current.key === 'onboarding' && (
            <StepOnboarding builder={builder} />
          )}
          {current.key === 'program' && (
            <StepProgram
              builder={builder}
              readOnly={program.status === 'active' && !devUnlock}
            />
          )}
          {current.key === 'membership' && (
            <StepMembership builder={builder} />
          )}
          {current.key === 'event' && <StepEvent builder={builder} />}
          {current.key === 'front' && <StepFront builder={builder} />}
          {current.key === 'back' && <StepBack builder={builder} />}
          {current.key === 'expiry' && (
            <StepMembershipExpiry
              builder={builder}
              readOnly={program.status === 'active' && !devUnlock}
            />
          )}
          {current.key === 'review' &&
            (isEvent ? (
              <StepEventReview
                builder={builder}
                onEditStep={jumpToStepKey}
                onRequestActivate={() => setConfirmOpen(true)}
              />
            ) : isMembership ? (
              <StepMembershipReview
                builder={builder}
                onEditStep={jumpToStepKey}
                onRequestActivate={() => setConfirmOpen(true)}
              />
            ) : (
              <StepReview
                builder={builder}
                onEditStep={jumpToStepKey}
                onRequestActivate={() => setConfirmOpen(true)}
              />
            ))}
        </div>

        {/* Nav */}
        <div className="program-builder-nav">
          <button
            type="button"
            className="owner-btn"
            disabled={stepIndex === 0}
            onClick={() => void goToStep(stepIndex - 1)}
          >
            Back
          </button>
          {cosmeticOnly && isLast ? (
            <button
              type="button"
              className="owner-btn owner-btn-primary"
              onClick={async () => {
                await builder.flush()
                toast.success('Appearance saved')
                navigate('/owner/program')
              }}
            >
              Done
            </button>
          ) : (
            !isLast && (
              <button
                type="button"
                className="owner-btn owner-btn-primary"
                onClick={() => void goToStep(stepIndex + 1)}
              >
                Next
              </button>
            )
          )}
        </div>

        {/* Mobile preview sheet */}
        <PassPreviewPanel
          program={preview}
          variant="sheet"
          desiredSide={previewSide}
        />
      </div>

      {/* Desktop preview */}
      <PassPreviewPanel
        program={preview}
        variant="side"
        desiredSide={previewSide}
      />

      {confirmOpen && (
        <ActivateConfirmModal
          isActivating={activate.isPending}
          isMembership={isMembership}
          onConfirm={() => void doActivate()}
          onCancel={() => setConfirmOpen(false)}
        />
      )}
    </div>
  )
}
